import React, { useEffect, useState } from "react";
import axios from "../../utils/axios";
import { getAuth } from "firebase/auth";

function WorkloadBadge({ value }) {
  const n = Number(value || 0);

  let cls =
    "bg-green-50 text-green-700 border-green-200 dark:bg-green-950/20 dark:text-green-300 dark:border-green-900";

  if (n >= 150) {
    cls = "bg-red-50 text-red-700 border-red-200 dark:bg-red-950/20 dark:text-red-300 dark:border-red-900";
  } else if (n >= 100) {
    cls = "bg-yellow-50 text-yellow-700 border-yellow-200 dark:bg-yellow-950/20 dark:text-yellow-300 dark:border-yellow-900";
  }

  return (
    <span className={`px-2 py-1 rounded-full border text-xs font-medium ${cls}`}>
      {n}% allocation
    </span>
  );
}

export default function ProjectWorkloadCard({ projectId }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  async function load() {
    try {
      setLoading(true);
      const token = await getAuth().currentUser.getIdToken();

      const res = await axios.get(`/project/${projectId}/workload`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      setItems(res.data?.items || []);
    } catch (err) {
      console.error("Failed to load workload:", err);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    if (projectId) load();
  }, [projectId]);

  return (
    <div className="mb-6 border border-gray-200 dark:border-neutral-700 rounded-2xl p-4 bg-white dark:bg-neutral-900">
      <div className="mb-4">
        <h2 className="text-lg font-bold text-black dark:text-white">Team Workload</h2>
        <p className="text-sm text-gray-500 dark:text-neutral-400">
          Resource load and assigned tasks by person.
        </p>
      </div>

      {loading ? (
        <div className="text-sm text-gray-500 dark:text-neutral-400">Loading workload...</div>
      ) : items.length === 0 ? (
        <div className="text-sm text-gray-500 dark:text-neutral-400">No workload data.</div>
      ) : (
        <div className="space-y-3">
          {items.map((row, idx) => (
            <div
              key={`${row.person_id || "unknown"}-${idx}`}
              className="border border-gray-200 dark:border-neutral-700 rounded-xl p-4"
            >
              <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
                <div>
                  <div className="font-medium text-black dark:text-white">
                    {row.person_name?.trim() || "Unknown person"}
                  </div>
                  <div className="text-sm text-gray-500 dark:text-neutral-400">
                    {row.email_work || "-"}
                  </div>
                </div>

                <div className="flex items-center gap-2 flex-wrap">
                  <WorkloadBadge value={row.allocation_percent_total} />
                  {Number(row.task_overdue || 0) > 0 && (
                    <span className="px-2 py-1 rounded-full border text-xs font-medium bg-red-50 text-red-700 border-red-200 dark:bg-red-950/20 dark:text-red-300 dark:border-red-900">
                      {row.task_overdue} overdue
                    </span>
                  )}
                </div>
              </div>

              <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mt-4 text-sm">
                <div className="p-3 rounded-xl bg-gray-50 dark:bg-neutral-950 border border-gray-200 dark:border-neutral-800">
                  <div className="text-xs text-gray-500 dark:text-neutral-400">Tasks</div>
                  <div className="text-lg font-semibold text-black dark:text-white">{row.task_total || 0}</div>
                </div>
                <div className="p-3 rounded-xl bg-gray-50 dark:bg-neutral-950 border border-gray-200 dark:border-neutral-800">
                  <div className="text-xs text-gray-500 dark:text-neutral-400">Todo</div>
                  <div className="text-lg font-semibold text-black dark:text-white">{row.task_todo || 0}</div>
                </div>
                <div className="p-3 rounded-xl bg-gray-50 dark:bg-neutral-950 border border-gray-200 dark:border-neutral-800">
                  <div className="text-xs text-gray-500 dark:text-neutral-400">In progress</div>
                  <div className="text-lg font-semibold text-black dark:text-white">{row.task_in_progress || 0}</div>
                </div>
                <div className="p-3 rounded-xl bg-gray-50 dark:bg-neutral-950 border border-gray-200 dark:border-neutral-800">
                  <div className="text-xs text-gray-500 dark:text-neutral-400">Done</div>
                  <div className="text-lg font-semibold text-black dark:text-white">{row.task_done || 0}</div>
                </div>
                <div className="p-3 rounded-xl bg-gray-50 dark:bg-neutral-950 border border-gray-200 dark:border-neutral-800">
                  <div className="text-xs text-gray-500 dark:text-neutral-400">Assignments</div>
                  <div className="text-lg font-semibold text-black dark:text-white">{row.resource_assignments || 0}</div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}